import React, { useState } from 'react';
import { Select, Button, Header, Form } from 'semantic-ui-react';

import { RangeDateInput } from '../../components/FiltersComponent/FilterField';
import { client } from '../../shared/client';
import { url } from '../../shared/URL';
import { downloadBlob, getFileNameFromHeader } from '../../shared/utils';
import { format } from 'date-fns';

import './reports.scss';

type ReportOption =
  | ''
  | 'SALE'
  | 'USER'
  | 'CHARGE'
  | 'MOTIVATION'
  | 'DISCHARGE'
  | 'SUPPORT'
  | 'CONSOLIDATED'
  | undefined;

const ReportsPage = () => {
  const [reportType, setReportType] = useState<ReportOption>();
  const [period, setPeriod] = useState<{ from: string; to: string }>();
  const [loading, setLoading] = useState(false);
  const [motivationKind, setMotivationKind] = useState('');
  const [participantId, setParticipantId] = useState('');
  const isMotivation = reportType === 'MOTIVATION';
  const invalidParticipant =
    isMotivation && !!participantId && !/^[1-9][0-9]*$/.test(participantId);
  const invalidPeriod = !!(
    period &&
    period.from &&
    period.to &&
    period.from > period.to
  );

  const _getReports = () => {
    if (invalidParticipant || invalidPeriod || loading) return;
    setLoading(true);
    const dateFilter = getDateFilter();
    const params = new URLSearchParams();
    if (period && period.from) params.set('from', period.from);
    if (period && (period.from || period.to)) {
      params.set('to', period.to || format(new Date(), 'yyyy-MM-dd'));
    }
    if (motivationKind) params.set('kind', motivationKind);
    if (participantId) params.set('userId', participantId);
    const requestUrl = isMotivation
      ? `${url.getReports}/motivation?${params.toString()}`
      : `${url.getReports}/?type=${reportType}${dateFilter}`;
    client
      .get(requestUrl, isMotivation ? { responseType: 'blob' } : undefined)
      .then((response) => {
        const fileName = getFileNameFromHeader(
          response.headers['content-disposition'],
          isMotivation ? 'motivation.xlsx' : 'report.csv'
        );
        const blob = new Blob([response.data]);
        downloadBlob(blob, fileName);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const getDateFilter = (): string => {
    // если не указана вторая дата в периоде (to), отправить сегодняшний день
    const to = (period && period.to) || format(new Date(), 'yyyy-MM-dd');
    const from = period && period.from;
    const prefix = 'createdTime';
    return period && from ? `&filters=${prefix}$${from}_${to}` : ``;
  };

  return (
    <div className="page">
      <div className="reports">
        <div className="reports__filters filters">
          <Header as="h1" className="reports__header">
            Отчеты
          </Header>
          <Form>
            <Select
              onChange={({}, data) => setReportType(data.value as ReportOption)}
              options={options}
              placeholder="Выберите тип отчета"
            />
            <RangeDateInput
              onChange={({}, { value }) => setPeriod(value)}
              name="reportTime"
            />
            {isMotivation && (
              <>
                <p>
                  Начисления за мотивацию: детализация и итоги в Excel. Период —
                  по дате начисления.
                </p>
                <Form.Select
                  label="Вид мотивации"
                  value={motivationKind}
                  onChange={({}, data) =>
                    setMotivationKind(String(data.value || ''))
                  }
                  options={motivationOptions}
                />
                <Form.Input
                  label="ID участника"
                  placeholder="Все участники"
                  value={participantId}
                  onChange={({}, data) => setParticipantId(data.value.trim())}
                  error={
                    invalidParticipant
                      ? 'Укажите положительный целый ID участника'
                      : false
                  }
                />
              </>
            )}
            {invalidPeriod && (
              <p role="alert">
                Дата начала не должна быть позже даты окончания.
              </p>
            )}
            <Button
              onClick={_getReports}
              loading={loading}
              disabled={
                !reportType || invalidParticipant || invalidPeriod || loading
              }
            >
              Выгрузить
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

const options = [
  {
    key: 'sale',
    value: 'SALE',
    text: 'Отчет по продажам',
  },
  {
    key: 'user',
    value: 'USER',
    text: 'Отчет по пользователям',
  },
  {
    key: 'charge',
    value: 'CHARGE',
    text: 'Отчет по начислению баллов',
  },
  {
    key: 'motivation',
    value: 'MOTIVATION',
    text: 'Отчет по начислениям за мотивацию (Excel)',
  },
  {
    key: 'discharge',
    value: 'DISCHARGE',
    text: 'Отчет по выводу вознаграждений',
  },
  {
    key: 'support',
    value: 'SUPPORT',
    text: 'Отчет по поддержке',
  },
  {
    key: 'consolidated',
    value: 'CONSOLIDATED',
    text: 'Сводный отчет',
  },
  {
    key: 'retailPoint',
    value: 'RETAIL_POINT',
    text: 'Отчет по РТ',
  },
];

const motivationOptions = [
  { key: 'all', value: '', text: 'Все виды мотивации' },
  { key: 'first', value: 'FIRST_SALE_MONTH', text: 'Первая продажа месяца' },
  { key: 'mission', value: 'MISSION_COMPLETE', text: 'Выполнение миссии' },
  {
    key: 'loyalty',
    value: 'LOYALTY_UPGRADE',
    text: 'Повышение категории лояльности',
  },
  { key: 'sleep', value: 'SLEEP_BONUS', text: 'Возврат в программу' },
];

export default ReportsPage;
